import * as React from 'react';
import { AppStore } from '../stores/AppStore';
import { inject, observer } from 'mobx-react';
import { Helper } from '../../../Helper';
import { IAppComponent } from '../../../interfaces/IAppComponent';
import SharepointService from '../../../services/SharepointService';
import { IACardViewService } from '../../messagesCardView/CardView.service';
import { IACard } from '../../card/Card';
import * as styles from "./ContentArea.css";
import { LocalizationService } from '../../../services/LocalizationService';
import { ISharepointNews, ISharepointNewsResult } from '../../../interfaces/ISharepointNews';
import { IAMessagesListView } from '../../messagesListView/MessagesListView';
import { IARotator } from '../../rotator/Rotator';

export interface ISharepointNewsProps {
  store?: AppStore;
  pageIndex?: number;
  componentIndex?: number;
  component?: IAppComponent;
}

export interface ISharepointNewsState {
  hideComponent: boolean;
  items: ISharepointNews[];
  count?: number;
  isLoading: boolean;
  skip: number;
  siteCollection?: string;
  numberOfCards?: number;
}

@inject('store')
@observer
export class SharepointNews extends React.Component<ISharepointNewsProps, ISharepointNewsState> {

  private cardViewService: IACardViewService = new IACardViewService();
  private cachedNewsKey;
  private localizationService: LocalizationService = new LocalizationService();

  constructor(props: ISharepointNewsProps) {
    super(props);
    this.state = {
      hideComponent: undefined,
      isLoading: undefined,
      skip: 0,
      items: []
    }
    this.cachedNewsKey = `sharepointNews-${props.pageIndex}-${props.componentIndex}`;
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public componentDidMount(): void {
    this.getCachedNews();
    this.getNews();
  }

  public componentWillReceiveProps(props: ISharepointNewsProps): void {
    if (props.component?.siteCollection !== this.state.siteCollection || props.component?.numberOfCards !== this.state.numberOfCards) {
      this.setState({
        items: [],
        count: undefined,
        skip: 0,
        numberOfCards: props.component?.numberOfCards
      }, () => this.getNews())
    }
  }

  private getCachedNews(): void {
    const cachedNews = localStorage.getItem(this.cachedNewsKey);
    if (cachedNews) {
      this.setState({
        items: JSON.parse(cachedNews)
      });
    }
  }

  private getNews(): void {
    if (this.state.isLoading) {
      return;
    }
    this.setState({ isLoading: true, siteCollection: this.props.component?.siteCollection });
    SharepointService.getNewsFromSite(this.props.store.environment, this.props.store.tenant, this.props.component?.siteCollection, this.state.skip, this.props.component?.numberOfCards).then((newsResult: ISharepointNewsResult) => {
      if (newsResult) {
        this.setState({
          items: this.state.skip === 0 ? newsResult.value : [...this.state.items, ...newsResult.value],
          count: newsResult.count,
          isLoading: false,
          skip: this.state.skip + this.props.component?.numberOfCards
        }, () => {
          if (this.state.items?.length <= this.props.component?.numberOfCards) {
            Helper.setLocalStorage(this.cachedNewsKey, JSON.stringify(newsResult.value));
          }
        });
      } else {
        this.setState({
          items: [],
          count: undefined,
          isLoading: false,
          skip: 0
        });
      }
    });
  }

  render(): JSX.Element {
    let messagesToDisplay = [];
    if (this.state.items != undefined) {
      // Resizing cards
      const [numberOfCardsPerRow, maxCardWidthAvailable] = this.cardViewService.getNumberOfCardsPerRowAndMaxCardWidthAvailable(300, 20, 300, 1);
      let currentRowNumber = 1;
      let cardsOnPage = 0;
      this.state.items.forEach((item: ISharepointNews, itemIndex) => {
        let image;
        if (item.thumbnailWebUrl && item.thumbnailWebUrl.indexOf("sitepagethumbnail") == -1) {
          if (item.thumbnailWebUrl.indexOf(this.props.store.tenant) != -1 && item.thumbnailWebUrl.indexOf("path=") != -1) {
            const splittetImage = item.thumbnailWebUrl.split("path=");
            if (splittetImage.length = 2) {
              image = `https://${this.props.store.tenant}.sharepoint.com${decodeURIComponent(splittetImage[1])}`;
            }
          } else {
            image = decodeURIComponent(item.thumbnailWebUrl);
          }
        }

        let link: string;
        if (item.webUrl.indexOf('https://') == 0) {
          link = item.webUrl;
        } else {
          if (this.props.component?.siteCollection === "rootsite") {
            link = `https://${this.props.store.tenant}.sharepoint.com/${decodeURIComponent(item.webUrl)}`;
          } else {
            link = `https://${this.props.store.tenant}.sharepoint.com/sites/${this.props.component?.siteCollection}/${decodeURIComponent(item.webUrl)}`;
          }
        }

        if (this.props.component?.display === "RotatorView") {
          messagesToDisplay.push(
            {
              id: itemIndex.toString(),
              tenant: this.props.store.tenant,
              token: this.props.store.token,
                environment: this.props.store.environment,
              modifiedDate: item.lastModifiedDateTime,
              title: item.title,
              teaser: item.description,
              item: item,
              image: image,
              itemTitleColor: this.props.component?.textColor ?? "#333333",
              itemTextColor: this.props.component?.textColor ?? "#333333",
              borderColor: this.props.component?.borderColor ?? undefined,
              borderRadius: this.props.component?.borderRadius ?? 5,
              showSkeleton: true,
              highlightColor: this.props.store.instance.settings.design.contentAreaHighlightColor ?? "blue",
              backgroundColor: this.props.component?.backgroundColor ?? "#ffffff",
              link: link,
              onClickOpen: () => { }
            }
          );
        } else {
          messagesToDisplay.push(
            <a
              key={itemIndex.toString()}
              href={link}
              target="_blank"
              style={{
                height: "fit-content",
                float: "left",
                width: "100%"
              }}
            >
              {this.props.component?.display === "Card view" &&
                <IACard
                  id={itemIndex.toString()}
                  token={this.props.store.token}
                  tenant={this.props.store.tenant}
                  environment={this.props.store.environment}
                  user={this.props.store.user}
                  title={item.title}
                  teaser={item.description}
                  date={this.props.component?.showStartDate && item.lastModifiedDateTime != undefined ? item.lastModifiedDateTime : undefined}
                  cover={image ? {
                    imageURL: image
                  } : undefined}
                  design={{
                    textColor: this.props.component?.textColor ?? "#333333",
                    highlightColor: this.props.store.instance.settings.design.contentAreaHighlightColor ?? "blue",
                    backgroundColor: this.props.component?.backgroundColor ?? "#ffffff",
                    showDropShadow: this.props.component?.showDropShadow ?? true,
                    borderRadius: this.props.component?.borderRadius ?? 5,
                    borderColor: this.props.component?.borderColor
                  }}
                  width={window.innerWidth < 667 ? window.innerWidth - 56 : maxCardWidthAvailable}
                  isMobile
                  styles={{
                    marginRight: currentRowNumber == numberOfCardsPerRow ? 0 : 20,
                    marginBottom: 20,
                    width: "100%"
                  }}
                  onClickOpen={() => { }}
                />
              }
              {this.props.component?.display === "List view" &&
                <IAMessagesListView
                  key={itemIndex.toString()}
                  id={itemIndex.toString()}
                  tenant={this.props.store.tenant}
                  component="Sharepoint News"
                  source="App"
                  token={this.props.store.token}
                  environment={this.props.store.environment}
                  modifiedDate={item.lastModifiedDateTime}
                  title={item.title}
                  teaser={item.description}
                  image={image}
                  itemTitleColor={this.props.component?.textColor ?? "#333333"}
                  itemTextColor={this.props.component?.textColor ?? "#333333"}
                  showSkeleton={false}
                  showImage
                  showTeaser
                  showTitle
                  showStartDate={this.props.component?.showStartDate && item.lastModifiedDateTime != undefined}
                  borderRadius={this.props.component?.borderRadius ?? 5}
                  highlightColor={this.props.store.instance.settings.design.contentAreaHighlightColor ?? "blue"}
                  backgroundColor={this.props.component?.backgroundColor ?? "#ffffff"}
                  borderColor={this.props.component?.borderColor ?? undefined}
                  showDropShadow={this.props.component?.showDropShadow ?? false}
                />
              }
            </a>
          );
          cardsOnPage++;
          if (currentRowNumber == numberOfCardsPerRow || cardsOnPage == 4) {
            currentRowNumber = 1;
          } else {
            currentRowNumber++;
          }
          if (cardsOnPage == 4) {
            cardsOnPage = 0;
          }
        }
      });
    }
    return (
      this.state.hideComponent ? <></> :
        <div
          key={`component_${Helper.getRandomStringKey()}`}
          style={{
            padding: 25
          }}
        >
          <div
            className={styles.IA_SharepointNewsTitle}
          >
            <span role="heading" aria-level={2}>
              {this.props.component.title}
            </span>
          </div>
          {this.props.component?.display === "RotatorView" ?
            <IARotator
              data={messagesToDisplay}
              showSkeleton={messagesToDisplay == undefined}
              showTitle
              width={this.props.store.preview ? 300 : window.innerWidth - 50}
              showTeaser
              showStartDate={this.props.component?.showStartDate}
              borderRadius={this.props.component?.borderRadius ?? 5}
              borderColor={this.props.component?.borderColor ?? undefined}
              showDropShadow={this.props.component?.showDropShadow ?? false}
              isMobile
              highlightColor={this.props.store.instance.settings.design.contentAreaHighlightColor ?? "blue"}
              hasDarkBackground={false}
              showPaging={false}
              tenant={this.props.store.tenant}
              token={this.props.store.token}
              environment={this.props.store.environment}
            />
            :
            messagesToDisplay
          }
          {messagesToDisplay.length === 0 && !this.state.isLoading &&
            <div
              style={{
                textAlign: "center",
                color: "gray"
              }}
            >
              <img src="https://intraactivestorage.blob.core.windows.net/cdn/SDK%20Images/no-content.png" width={"100%"} />
              {this.localizationService.strings.AppAdmin_NoSharepointNews}
            </div>
          }

          {!this.state.isLoading && this.state.count > this.state.items.length && this.props.component?.display !== "RotatorView" &&
            <div
              className={styles.IA_paging}
            >
              <div
                className={styles.loadMoreButton}
                style={{
                  color: this.props.store.instance?.settings?.design?.contentAreaHighlightColor
                }}
                onClick={() => this.getNews()}
              >
                {new LocalizationService().strings.Phonebook_LoadMore}
              </div>
            </div>
          }
          {this.state.isLoading &&
            <div className={styles.IA_loading}>
              <div className={styles.loader} />
            </div>
          }
        </div>
    );
  }
}